import { useEffect, useRef, useCallback } from 'react';
import NoSleep from 'nosleep.js';

const useNoSleep = () => {
  // Direct device detection without dependency on ScreenProvider
  const userAgent = navigator.userAgent.toLowerCase();
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  
  let deviceType = 'desktop';
  let supportsAdvancedFeatures = true;
  
  if (userAgent.includes('tv') || userAgent.includes('webos') || userAgent.includes('tizen') ||
      userAgent.includes('roku') || userAgent.includes('chromecast') || userAgent.includes('smarttv') ||
      /Silk|AFT[A-Z]/i.test(navigator.userAgent)) {  // AFT followed by any letter covers all Fire TV models
    deviceType = 'tv';
    supportsAdvancedFeatures = false;
  } else if (maxTouchPoints > 1 && (userAgent.includes('tablet') || userAgent.includes('ipad') || 
             (userAgent.includes('android') && !userAgent.includes('mobile')))) {
    deviceType = 'tablet';
    supportsAdvancedFeatures = true;
  } else if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
    deviceType = 'mobile';
    supportsAdvancedFeatures = true;
  }
  
  const noSleepRef = useRef(null);
  const isEnabledRef = useRef(false);
  const wakeLockRef = useRef(null);
  const invisibleVideoRef = useRef(null);
  const activityIntervalRef = useRef(null);
  const wakeLockRetryTimeoutRef = useRef(null); // Track retry timeout to prevent memory leak
  const isMountedRef = useRef(true); // Track if component is mounted
  const simulationTimeoutsRef = useRef([]); // Track all simulation timeouts to prevent memory leak
  // MEMORY FIX: Exponential backoff for WakeLock retries
  const wakeLockRetryDelayRef = useRef(1000); // Start at 1s, doubles up to 60s
  const wakeLockRetryCountRef = useRef(0); // Track retry count
  const MAX_WAKELOCK_RETRIES = 10;
  const MAX_WAKELOCK_RETRY_DELAY = 60000; // 60s cap
  
  // Detect Fire TV and other TV browsers
  const isFireTV = /Silk|AFT[A-Z]/i.test(navigator.userAgent);  // AFT followed by any letter covers all Fire TV models
  const isSamsungTV = /SMART-TV|Tizen/.test(navigator.userAgent);
  const isLGTV = /Web0S|webOS/.test(navigator.userAgent);
  const isTV = deviceType === 'tv' || isFireTV || isSamsungTV || isLGTV;
  
  // Initialize NoSleep
  const initializeNoSleep = useCallback(() => {
    if (!noSleepRef.current) {
      noSleepRef.current = new NoSleep();
      // Reduced logging - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log("[useNoSleep] NoSleep initialized");
      }
    }
  }, []);
  
  // Create invisible video for Fire TV keep-alive
  const createInvisibleVideo = useCallback(() => {
    if (invisibleVideoRef.current) return invisibleVideoRef.current;
    
    const video = document.createElement('video');
    video.style.cssText = 'position:fixed;top:-100px;left:-100px;width:1px;height:1px;opacity:0;pointer-events:none;';
    // Minimal base64 encoded 1-second video
    video.src = 'data:video/mp4;base64,AAAAHGZ0eXBpc29tAAACAGlzb21pc28ybXA0MQAAAAhmcmVlAAAAGm1kYXQAAAGzABAHAAABthADAowdbb9/AAAC6W1vb3YAAABsbXZoZAAAAAB8JbCAfCWwgAAAA+gAAAAAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAIVdHJhawAAAFx0a2hkAAAAD3wlsIB8JbCAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAABUG1kaWEAAAAgbWRoZAAAAAB8JbCAfCWwgAAArEQAAWAAVcQAAAAAAC1oZGxyAAAAAAAAAAB2aWRlAAAAAAAAAAAAAAAAVmlkZW9IYW5kbGVyAAAAAQVtaW5mAAAAFHZtaGQAAAABAAAAAAAAAAAAAAAkZGluZgAAABxkcmVmAAAAAAAAAAEAAAAMdXJsIAAAAAEAAADFc3RibAAAALFzdHNkAAAAAAAAAAEAAAChdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAIAAgABIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAANWVzZHMAAAAAA4CAgAAAABVkcs6QAAAGCkgAAESAAAADAQAAAAAAAAABAAAAAAAAAAAABQAAAlhzdHNzAAAAAAAAAAEAAAABAAAAgAAAAABjdHRzAAAAAAAAAAsAAAABAAABAAAAAAEAAAIAAAAAAQAAAQAAAAABAAADAAAAAAEAAAEAAAAAAQABgAAAAAEAAAIAAAAAAQAAAQAAAAABAAADAAAAAAEAAAEAAAAAAQABgAAAAAEAAAPAAAAAAQAAAQAAAAAVc3RzYwAAAAAAAAABAAAAAQAAAAEAAAABAAAAHHN0c3oAAAAAAAAA1AAAAAEAAAAUc3RjbwAAAAAAAAABAAADIAAAABZ1ZHRhAAAADm5hbWVjYmNzX2luaXQAAAApZW5jdAAAAABicm94AAAAU2Jyb3hfYXVkaW9fbW9kZT1zdGVyZW87';
    video.loop = true;
    video.muted = true;
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    
    document.body.appendChild(video);
    invisibleVideoRef.current = video;
    
    // Start playing
    video.play().catch(err => {
      console.info('[useNoSleep] Video autoplay blocked:', err.message);
    });
    
    // Reduced logging - only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[useNoSleep] Invisible video created for TV keep-alive');
    }
    return video;
  }, []);
  
  // Helper to clear all simulation timeouts
  const clearSimulationTimeouts = useCallback(() => {
    if (simulationTimeoutsRef.current.length > 0) {
      simulationTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      simulationTimeoutsRef.current = [];
    }
  }, []);

  // Helper to track a simulation timeout
  const trackTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      // Remove from tracking array when executed
      const index = simulationTimeoutsRef.current.indexOf(timeoutId);
      if (index > -1) {
        simulationTimeoutsRef.current.splice(index, 1);
      }
      callback();
    }, delay);
    simulationTimeoutsRef.current.push(timeoutId);
    return timeoutId;
  }, []);

  // Hidden DOM pulse to keep low-power TV browsers active without spoofing user input
  const simulateDOMActivity = useCallback(() => {
    // Clear any pending simulation timeouts before starting new ones
    clearSimulationTimeouts();

    const pulseTokens = ['0', '1', '2'];

    // Strategy 1: Toggle multiple invisible elements to trigger reflows
    ['wake-keeper-1', 'wake-keeper-2', 'wake-keeper-3'].forEach((id, index) => {
      trackTimeout(() => {
        let keeper = document.getElementById(id);
        if (!keeper) {
          keeper = document.createElement('div');
          keeper.id = id;
          keeper.style.cssText = `position:fixed;top:-${index + 1}px;left:-${index + 1}px;width:1px;height:1px;pointer-events:none;z-index:-1;opacity:0.01;`;
          document.body.appendChild(keeper);
        }

        const currentPulse = keeper.dataset.wakePulse || pulseTokens[pulseTokens.length - 1];
        const nextPulse = pulseTokens[(pulseTokens.indexOf(currentPulse) + 1) % pulseTokens.length];
        keeper.dataset.wakePulse = nextPulse;
        keeper.style.backgroundColor = nextPulse === '0'
          ? 'transparent'
          : nextPulse === '1'
            ? 'rgba(0, 0, 0, 0.01)'
            : 'rgba(255, 255, 255, 0.01)';
        keeper.style.transform = nextPulse === '1' ? 'translate3d(0, 0, 0)' : 'translate3d(0.5px, 0, 0)';

        // Force layout calculation
        void keeper.offsetHeight;
      }, index * 75);
    });

    // Strategy 2: Nudge the hidden keep-alive video if the browser paused it
    if (invisibleVideoRef.current) {
      trackTimeout(() => {
        const keepAliveVideo = invisibleVideoRef.current;
        keepAliveVideo.style.opacity = keepAliveVideo.style.opacity === '0.01' ? '0.02' : '0.01';
        void keepAliveVideo.offsetHeight;

        if (keepAliveVideo.paused) {
          keepAliveVideo.play().catch(() => {});
        }
      }, 250);
    }

    console.debug(`[useNoSleep] Hidden keep-alive pulse applied for ${isFireTV ? 'Fire TV' : isTV ? 'TV' : 'device'}`);
  }, [isTV, isFireTV, clearSimulationTimeouts, trackTimeout]);

  // Enable screen wake lock with multi-layered strategies
  const enableWakeLock = useCallback(async () => {
    // Reduced logging - only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[useNoSleep] Enabling wake lock for ${isFireTV ? 'Fire TV' : isTV ? 'TV' : deviceType}`);
    }
    
    // Strategy 1: Try Wake Lock API first (may not work on all TVs, but worth trying)
    if ('wakeLock' in navigator && supportsAdvancedFeatures) {
      try {
        // Only request WakeLock if page is visible - prevents NotAllowedError
        if (document.visibilityState !== 'visible') {
          console.info('[useNoSleep] Page not visible, skipping Wake Lock request');
        } else {
          if (wakeLockRef.current) {
            await wakeLockRef.current.release().catch(() => {});
          }
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          if (wakeLockRef.current) {
            // MEMORY FIX: Reset backoff on successful acquisition
            wakeLockRetryDelayRef.current = 1000;
            wakeLockRetryCountRef.current = 0;
            // Reduced logging - only log in development
            if (process.env.NODE_ENV === 'development') {
              console.log('[useNoSleep] Wake Lock API enabled');
            }
            wakeLockRef.current.addEventListener('release', () => {
              // Reduced logging - only log in development
              if (process.env.NODE_ENV === 'development') {
                console.log('[useNoSleep] Wake Lock released, re-enabling...');
              }
              // MEMORY FIX: Exponential backoff retry with max attempts
              if (isMountedRef.current && wakeLockRetryCountRef.current < MAX_WAKELOCK_RETRIES) {
                if (wakeLockRetryTimeoutRef.current) {
                  clearTimeout(wakeLockRetryTimeoutRef.current);
                }
                const delay = wakeLockRetryDelayRef.current;
                wakeLockRetryTimeoutRef.current = setTimeout(() => {
                  if (isMountedRef.current) {
                    wakeLockRetryCountRef.current++;
                    // Double the delay for next retry, cap at MAX
                    wakeLockRetryDelayRef.current = Math.min(delay * 2, MAX_WAKELOCK_RETRY_DELAY);
                    enableWakeLock();
                  }
                }, delay);
              }
            });

            // For TVs, also use additional strategies
            if (!isTV) return;
          }
        }
      } catch (error) {
        // MEMORY FIX: Suppress NotAllowedError after first occurrence to reduce log/error spam
        if (error.name === 'NotAllowedError') {
          if (wakeLockRetryCountRef.current === 0) {
            console.info('[useNoSleep] Wake Lock not allowed (page not visible):', error.message);
          }
          // Don't count NotAllowedError toward retry limit but increase backoff
          wakeLockRetryDelayRef.current = Math.min(wakeLockRetryDelayRef.current * 2, MAX_WAKELOCK_RETRY_DELAY);
        } else {
          console.info('[useNoSleep] Wake Lock API not available:', error.message);
        }
      }
    }
    
    // Strategy 2: Video keep-alive for Fire TV and other TVs
    if (isTV || isFireTV) {
      try {
        createInvisibleVideo();
        // Reduced logging - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('[useNoSleep] Video keep-alive enabled for TV');
        }
      } catch (error) {
        console.warn('[useNoSleep] Failed to create video keep-alive:', error);
      }
    }
    
    // Strategy 3: NoSleep.js fallback
    try {
      if (!noSleepRef.current) {
        initializeNoSleep();
      }
      noSleepRef.current.enable();
      isEnabledRef.current = true;
      // Reduced logging - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[useNoSleep] NoSleep.js enabled');
      }
    } catch (error) {
      console.info('[useNoSleep] NoSleep.js failed:', error.message);
    }
    
    // Strategy 4: DOM activity for all TVs
    if (isTV || isFireTV) {
      // Clear existing interval
      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
      }
      
      // Start DOM activity simulation with more aggressive timing for TVs
      simulateDOMActivity(); // Run immediately
      const interval = isFireTV ? 8000 : isTV ? 10000 : 20000; // Much more frequent for TVs
      activityIntervalRef.current = setInterval(() => {
        simulateDOMActivity();
      }, interval);
      
              // Reduced logging - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log(`[useNoSleep] DOM activity simulation started for TV (${interval/1000}s interval)`);
        }
    }
  }, [supportsAdvancedFeatures, initializeNoSleep, createInvisibleVideo, simulateDOMActivity, isTV, isFireTV, deviceType]);

  // Disable screen wake lock
  const disableWakeLock = useCallback(async () => {
    try {
      // Release Wake Lock API
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        // Reduced logging - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log("[useNoSleep] Wake Lock disabled");
      }
      }
    } catch (error) {
      console.warn("[useNoSleep] Failed to release Wake Lock:", error);
    }
    
    // Disable NoSleep.js
    try {
      if (noSleepRef.current && isEnabledRef.current) {
        noSleepRef.current.disable();
        isEnabledRef.current = false;
        // Reduced logging - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log("[useNoSleep] NoSleep disabled");
        }
      }
    } catch (error) {
      console.error("[useNoSleep] Failed to disable NoSleep:", error);
    }
    
    // Remove invisible video
    if (invisibleVideoRef.current) {
      invisibleVideoRef.current.pause();
      invisibleVideoRef.current.remove();
      invisibleVideoRef.current = null;
              // Reduced logging - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log("[useNoSleep] Video keep-alive removed");
        }
    }
    
    // Clear activity interval
    if (activityIntervalRef.current) {
      clearInterval(activityIntervalRef.current);
      activityIntervalRef.current = null;
              // Reduced logging - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log("[useNoSleep] DOM activity simulation stopped");
        }
    }

    // Clear retry timeout
    if (wakeLockRetryTimeoutRef.current) {
      clearTimeout(wakeLockRetryTimeoutRef.current);
      wakeLockRetryTimeoutRef.current = null;
    }

    // Clear all simulation timeouts to prevent memory leak
    clearSimulationTimeouts();

    // Clean up keeper divs to prevent memory leak
    ['wake-keeper-1', 'wake-keeper-2', 'wake-keeper-3'].forEach((id) => {
      const keeper = document.getElementById(id);
      if (keeper) {
        keeper.remove();
      }
    });

    if (process.env.NODE_ENV === 'development') {
      console.log("[useNoSleep] Keeper divs cleaned up");
    }
  }, [clearSimulationTimeouts]);

  // Check if wake lock is active
  const isWakeLockActive = useCallback(() => {
    return (wakeLockRef.current && !wakeLockRef.current.released) || isEnabledRef.current;
  }, []);

  // Handle visibility change (re-enable wake lock when page becomes visible)
  const handleVisibilityChange = useCallback(async () => {
    if (document.visibilityState === 'visible') {
      // MEMORY FIX: Reset backoff on visibility change (fresh opportunity)
      wakeLockRetryDelayRef.current = 1000;
      wakeLockRetryCountRef.current = 0;
      // Reduced logging - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log("[useNoSleep] Page became visible, re-enabling wake lock");
      }
      await enableWakeLock();
    }
  }, [enableWakeLock]);

  // Auto-enable for certain device types
  const autoEnableForDevice = useCallback(async () => {
    if (deviceType === 'tv' || deviceType === 'tablet' || isTV || isFireTV) {
      // Reduced logging - only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[useNoSleep] Auto-enabling wake lock for ${isFireTV ? 'Fire TV' : deviceType}`);
      }
      await enableWakeLock();
    }
  }, [deviceType, enableWakeLock, isTV, isFireTV]);

  // Initialize
  useEffect(() => {
    isMountedRef.current = true;
    initializeNoSleep();

    // Auto-enable for certain devices
    autoEnableForDevice();

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMountedRef.current = false; // Mark as unmounted to prevent memory leaks
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disableWakeLock();
    };
  }, [initializeNoSleep, autoEnableForDevice, handleVisibilityChange, disableWakeLock]);

  // Handle page reload/unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Reduced logging - only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log("[useNoSleep] Page unloading, disabling wake lock");
    }
      disableWakeLock();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [disableWakeLock]);

  return {
    enableWakeLock,
    disableWakeLock,
    isWakeLockActive,
    isEnabled: isEnabledRef.current,
    supportsWakeLock: 'wakeLock' in navigator && supportsAdvancedFeatures,
    isFireTV,
    isTV
  };
};

export default useNoSleep; 
